import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  PROJECT_TEMPLATE_CONFIG,
  type ProjectTemplateRecipientType,
  type SenderStakeholderType,
} from '../../config/templateConfig'
import { useUser } from '../../contexts/UserContext'
import { useSaveToDirectory } from '../../hooks/useSaveToDirectory'
import { userService } from '../../services/userService'
import type { Project } from '../../types/project'
import type { Contact, User } from '../../types/user'
import { StakeholderType, UserType } from '../../types/user'
import SaveToDirectoryDialog from '../contact/SaveToDirectoryDialog'
import ParentProjectSelector from './ParentProjectSelector'

// Module-level cache for contact data to prevent refetching on remount
let cachedTemplateContactData: {
  teamMembers: User[]
  brokers: Contact[]
  subHaulers: Contact[]
  contractors: Contact[]
} | null = null

// Debug: Track component instance
const templateContactInfoInstanceCount = 0
import ContactAutocomplete, { type ContactInfo } from '../ContactAutocomplete'
import Input from '../Input'
import MultiSelect, { type Option } from '../MultiSelect'

interface TemplateContactInfoStepProps {
  data: Partial<Project>
  onUpdate: (data: Partial<Project>) => void
  readOnly?: boolean
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
  templateType: ProjectTemplateRecipientType
  // Sub-contract flow props (sub-hauler to sub-hauler)
  isSubContract?: boolean
  // Broker downstream flow props (broker to broker)
  isBrokerDownstream?: boolean
  parentProject?: Project | null
  onParentProjectSelect?: (project: Project | null) => void
  userStakeholderType?: SenderStakeholderType | null
}

const TemplateContactInfoStep: React.FC<TemplateContactInfoStepProps> = ({
  data,
  onUpdate,
  readOnly = false,
  onValidationChange,
  showErrors = false,
  templateType,
  isSubContract = false,
  isBrokerDownstream = false,
  parentProject = null,
  onParentProjectSelect,
  userStakeholderType,
}) => {
  const { user: currentUser } = useUser()

  // Save-to-directory workflow for site supervisor
  const {
    showDialog: showSiteSupervisorDialog,
    pendingContact: pendingSiteSupervisorContact,
    checkAndPrompt: checkSiteSupervisorSaveToDirectory,
    handleConfirm: handleSiteSupervisorDialogConfirm,
    handleCancel: handleSiteSupervisorDialogCancel,
  } = useSaveToDirectory()

  // Team members from the user's org (for Contractor field)
  const [orgContractors, setOrgContractors] = useState<User[]>([])
  // Contacts (for recipient fields - Broker, Sub Hauler, Third Party Contractor)
  const [brokers, setBrokers] = useState<Contact[]>([])
  const [subHaulers, setSubHaulers] = useState<Contact[]>([])
  const [thirdPartyContractors, setThirdPartyContractors] = useState<Contact[]>(
    [],
  )
  // Project managers from team members
  const [orgProjectManagers, setOrgProjectManagers] = useState<User[]>([])
  // Administrators from team members
  const [orgAdministrators, setOrgAdministrators] = useState<User[]>([])
  // Site supervisors from team members
  const [siteSupervisors, setSiteSupervisors] = useState<
    {
      id: number
      firstName: string
      lastName: string
      email: string
      phone?: string
    }[]
  >([])
  // Loading state for API calls
  const [isLoadingData, setIsLoadingData] = useState(true)
  // Ref to prevent refetching if component remounts
  const hasFetchedRef = useRef(false)

  const templateConfig = PROJECT_TEMPLATE_CONFIG[templateType]

  // Fetch team members and contacts on mount
  useEffect(() => {
    // Helper to process and set state from data
    const processData = (
      teamMembers: User[],
      brokerData: Contact[],
      subHaulerData: Contact[],
      contractorContacts: Contact[],
    ) => {
      // Filter team members to get contractors (OWNER user type with CONTRACTOR stakeholder)
      // Only OWNER-level users should appear in the Contractor field
      // Treat missing user_type as OWNER (backend default)
      const contractorsInOrg = teamMembers.filter((member) => {
        const userType = member.user_type || UserType.OWNER
        const hasValidUserType = userType === UserType.OWNER
        const hasContractorStakeholder =
          member.current_profile?.stakeholder_type ===
            StakeholderType.CONTRACTOR ||
          member.stakeholder_types?.includes(
            StakeholderType.CONTRACTOR as unknown as string,
          )
        return hasValidUserType && hasContractorStakeholder
      })
      setOrgContractors(contractorsInOrg)

      // Filter team members to get site supervisors
      const supervisors = teamMembers
        .filter((member) => member.user_type === UserType.SITE_SUPERVISOR)
        .map((member) => ({
          id: member.id,
          firstName: member.first_name,
          lastName: member.last_name,
          email: member.email,
          phone: member.phone_number,
        }))
      setSiteSupervisors(supervisors)

      // Filter team members to get project managers
      const projectManagersInOrg = teamMembers.filter(
        (member) => member.user_type === UserType.PROJECT_MANAGER,
      )
      setOrgProjectManagers(projectManagersInOrg)

      // Filter team members to get administrators
      const administratorsInOrg = teamMembers.filter(
        (member) => member.user_type === UserType.ADMINISTRATOR,
      )
      setOrgAdministrators(administratorsInOrg)

      // Filter contacts to only show owners
      setBrokers(
        brokerData.filter((c) => c.contact_object.user_type === UserType.OWNER),
      )
      setSubHaulers(
        subHaulerData.filter(
          (c) => c.contact_object.user_type === UserType.OWNER,
        ),
      )
      // Third party contractors are external contacts
      setThirdPartyContractors(
        contractorContacts.filter(
          (c) => c.contact_object.user_type === UserType.OWNER,
        ),
      )
    }

    // Use cached data if available (prevents refetch on remount)
    if (cachedTemplateContactData) {
      const {
        teamMembers,
        brokers: brokerData,
        subHaulers: subHaulerData,
        contractors: contractorContacts,
      } = cachedTemplateContactData
      processData(teamMembers, brokerData, subHaulerData, contractorContacts)
      setIsLoadingData(false)
      return
    }

    // Skip if already fetching (ref guard)
    if (hasFetchedRef.current) {
      setIsLoadingData(false)
      return
    }
    hasFetchedRef.current = true

    const fetchData = async () => {
      setIsLoadingData(true)
      try {
        const [teamMembers, brokerData, subHaulerData, contractorContacts] =
          await Promise.all([
            userService.getTeamMembers(),
            userService.getContactsByType(['BROKER']),
            userService.getContactsByType(['SUB_HAULER']),
            userService.getContactsByType(['CONTRACTOR']),
          ])

        // Cache the raw data
        cachedTemplateContactData = {
          teamMembers,
          brokers: brokerData,
          subHaulers: subHaulerData,
          contractors: contractorContacts,
        }

        processData(teamMembers, brokerData, subHaulerData, contractorContacts)
      } catch (error) {
        console.error('Failed to fetch data:', error)
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchData()
  }, [])

  // Combined flag for any downstream flow (sub-hauler or broker)
  const isDownstreamFlow = isSubContract || isBrokerDownstream

  // Validation - requirements differ based on flow type and sender stakeholder type
  const isValid = useMemo(() => {
    // For sub-contract flow: parent project and sub-hauler recipient are required
    if (isSubContract) {
      const hasParentProject = !!parentProject
      const hasRecipient =
        templateType === 'THIRD_PARTY_SUB_HAULER'
          ? !!(
              data.third_party_sub_haulers &&
              data.third_party_sub_haulers.length > 0
            )
          : !!(data.sub_haulers && data.sub_haulers.length > 0)
      return hasParentProject && hasRecipient
    }

    // For broker downstream flow: parent project and broker recipient are required
    if (isBrokerDownstream) {
      const hasParentProject = !!parentProject
      const hasRecipient = data.brokers && data.brokers.length > 0
      return hasParentProject && hasRecipient
    }

    // Check recipient is selected based on template type
    let hasRecipient = false
    if (templateType === 'BROKER' || templateType === 'THIRD_PARTY_BROKER') {
      hasRecipient = data.brokers && data.brokers.length > 0
    } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
      hasRecipient =
        data.third_party_contractors && data.third_party_contractors.length > 0
    } else if (templateType === 'SUB_HAULER') {
      hasRecipient = data.sub_haulers && data.sub_haulers.length > 0
    } else if (templateType === 'THIRD_PARTY_SUB_HAULER') {
      hasRecipient =
        data.third_party_sub_haulers && data.third_party_sub_haulers.length > 0
    }

    // For CONTRACTOR senders: Contractor and recipient are required
    // For BROKER senders: Only recipient is required (no contractor needed)
    if (userStakeholderType === 'CONTRACTOR') {
      const hasContractor = data.contractors && data.contractors.length > 0
      return hasContractor && hasRecipient
    }

    // For brokers: only recipient is required
    return hasRecipient
  }, [
    data.contractors,
    data.brokers,
    data.third_party_contractors,
    data.sub_haulers,
    data.third_party_sub_haulers,
    templateType,
    isSubContract,
    isBrokerDownstream,
    parentProject,
    userStakeholderType,
  ])

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid)
    }
  }, [isValid, onValidationChange])

  // Convert contractor User to ContactInfo for ContactAutocomplete
  const contractorContactInfo: ContactInfo | null = useMemo(() => {
    const contractor = data.contractors?.[0]
    if (!contractor) return null

    // Build company address from structured fields if available
    let companyAddress = ''
    let companyName = contractor.company?.name

    if (contractor.company) {
      if (contractor.company.street_address) {
        const parts = []
        if (contractor.company.street_address)
          parts.push(contractor.company.street_address)
        if (contractor.company.city) parts.push(contractor.company.city)
        if (contractor.company.state) parts.push(contractor.company.state)
        if (contractor.company.zip_code) parts.push(contractor.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (contractor.company.address) {
        companyAddress = contractor.company.address
      }
    }

    // Use current user's company as fallback for team members without company data
    if (!companyName && currentUser?.company) {
      companyName = currentUser.company.name
      if (currentUser.company.street_address) {
        const parts = []
        if (currentUser.company.street_address)
          parts.push(currentUser.company.street_address)
        if (currentUser.company.city) parts.push(currentUser.company.city)
        if (currentUser.company.state) parts.push(currentUser.company.state)
        if (currentUser.company.zip_code)
          parts.push(currentUser.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (currentUser.company.address) {
        companyAddress = currentUser.company.address
      }
    }

    return {
      id: contractor.id,
      firstName: contractor.first_name || '',
      lastName: contractor.last_name || '',
      email: contractor.email,
      phone: contractor.phone_number,
      companyName,
      companyAddress,
      isFromDirectory: true,
    }
  }, [data.contractors, currentUser])

  // Convert project manager User to ContactInfo for ContactAutocomplete
  const projectManagerContactInfo: ContactInfo | null = useMemo(() => {
    const pm = data.project_managers?.[0]
    if (!pm) return null

    let companyAddress = ''
    let companyName = pm.company?.name

    if (pm.company) {
      if (pm.company.street_address) {
        const parts = []
        if (pm.company.street_address) parts.push(pm.company.street_address)
        if (pm.company.city) parts.push(pm.company.city)
        if (pm.company.state) parts.push(pm.company.state)
        if (pm.company.zip_code) parts.push(pm.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (pm.company.address) {
        companyAddress = pm.company.address
      }
    }

    if (!companyName && currentUser?.company) {
      companyName = currentUser.company.name
      if (currentUser.company.street_address) {
        const parts = []
        if (currentUser.company.street_address)
          parts.push(currentUser.company.street_address)
        if (currentUser.company.city) parts.push(currentUser.company.city)
        if (currentUser.company.state) parts.push(currentUser.company.state)
        if (currentUser.company.zip_code)
          parts.push(currentUser.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (currentUser.company.address) {
        companyAddress = currentUser.company.address
      }
    }

    return {
      id: pm.id,
      firstName: pm.first_name || '',
      lastName: pm.last_name || '',
      email: pm.email,
      phone: pm.phone_number,
      companyName,
      companyAddress,
      isFromDirectory: true,
    }
  }, [data.project_managers, currentUser])

  // Convert administrator User to ContactInfo for ContactAutocomplete
  const administratorContactInfo: ContactInfo | null = useMemo(() => {
    const admin = data.administrators?.[0]
    if (!admin) return null

    let companyAddress = ''
    let companyName = admin.company?.name

    if (admin.company) {
      if (admin.company.street_address) {
        const parts = []
        if (admin.company.street_address)
          parts.push(admin.company.street_address)
        if (admin.company.city) parts.push(admin.company.city)
        if (admin.company.state) parts.push(admin.company.state)
        if (admin.company.zip_code) parts.push(admin.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (admin.company.address) {
        companyAddress = admin.company.address
      }
    }

    if (!companyName && currentUser?.company) {
      companyName = currentUser.company.name
      if (currentUser.company.street_address) {
        const parts = []
        if (currentUser.company.street_address)
          parts.push(currentUser.company.street_address)
        if (currentUser.company.city) parts.push(currentUser.company.city)
        if (currentUser.company.state) parts.push(currentUser.company.state)
        if (currentUser.company.zip_code)
          parts.push(currentUser.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (currentUser.company.address) {
        companyAddress = currentUser.company.address
      }
    }

    return {
      id: admin.id,
      firstName: admin.first_name || '',
      lastName: admin.last_name || '',
      email: admin.email,
      phone: admin.phone_number,
      companyName,
      companyAddress,
      isFromDirectory: true,
    }
  }, [data.administrators, currentUser])

  // Convert recipient User to ContactInfo for ContactAutocomplete
  const recipientContactInfo: ContactInfo | null = useMemo(() => {
    let recipient: User | undefined
    if (templateType === 'BROKER' || templateType === 'THIRD_PARTY_BROKER') {
      recipient = data.brokers?.[0]
    } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
      recipient = data.third_party_contractors?.[0]
    } else if (templateType === 'SUB_HAULER') {
      recipient = data.sub_haulers?.[0]
    } else if (templateType === 'THIRD_PARTY_SUB_HAULER') {
      recipient = data.third_party_sub_haulers?.[0]
    }

    if (!recipient) return null

    // Build company address from structured fields if available
    let companyAddress = ''
    if (recipient.company) {
      if (recipient.company.street_address) {
        const parts = []
        if (recipient.company.street_address)
          parts.push(recipient.company.street_address)
        if (recipient.company.city) parts.push(recipient.company.city)
        if (recipient.company.state) parts.push(recipient.company.state)
        if (recipient.company.zip_code) parts.push(recipient.company.zip_code)
        companyAddress = parts.join(', ')
      } else if (recipient.company.address) {
        companyAddress = recipient.company.address
      }
    }

    return {
      id: recipient.id,
      firstName: recipient.first_name || '',
      lastName: recipient.last_name || '',
      email: recipient.email,
      phone: recipient.phone_number,
      companyName: recipient.company?.name,
      companyAddress,
      isFromDirectory: true,
    }
  }, [
    data.brokers,
    data.third_party_contractors,
    data.sub_haulers,
    data.third_party_sub_haulers,
    templateType,
  ])

  // Handle contractor selection from ContactAutocomplete
  const handleContractorChange = useCallback(
    (contact: ContactInfo | null) => {
      if (!contact) {
        onUpdate({ contractors: [] })
        return
      }

      // Find the matching contractor from orgContractors
      const matchingContractor = orgContractors.find(
        (c) => c.id === contact.id || c.email === contact.email,
      )

      if (matchingContractor) {
        // Use the email/phone from ContactAutocomplete value (may have been removed by user)
        // For company, use matchingContractor's company or fall back to currentUser's company
        // (team members share the same company)
        const updatedContractor = {
          ...matchingContractor,
          email: contact.email,
          phone_number: contact.phone || '',
          company: matchingContractor.company || currentUser?.company,
        }
        onUpdate({ contractors: [updatedContractor] })
      } else {
        // If not found (manual entry), create a partial user object
        const newUser = {
          id: contact.id || 0,
          first_name: contact.firstName,
          last_name: contact.lastName,
          email: contact.email,
          phone_number: contact.phone,
          company: contact.companyName
            ? {
                name: contact.companyName,
                address: contact.companyAddress,
              }
            : undefined,
        } as User
        onUpdate({ contractors: [newUser] })
      }
    },
    [orgContractors, currentUser, onUpdate],
  )

  // Handle project manager selection from ContactAutocomplete
  const handleProjectManagerChange = useCallback(
    (contact: ContactInfo | null) => {
      if (!contact) {
        onUpdate({ project_managers: [] })
        return
      }

      const matching = orgProjectManagers.find(
        (c) => c.id === contact.id || c.email === contact.email,
      )

      if (matching) {
        const updated = {
          ...matching,
          email: contact.email,
          phone_number: contact.phone || '',
          company: matching.company || currentUser?.company,
        }
        onUpdate({ project_managers: [updated] })
      } else {
        const newUser = {
          id: contact.id || 0,
          first_name: contact.firstName,
          last_name: contact.lastName,
          email: contact.email,
          phone_number: contact.phone,
          company: contact.companyName
            ? {
                name: contact.companyName,
                address: contact.companyAddress,
              }
            : undefined,
        } as User
        onUpdate({ project_managers: [newUser] })
      }
    },
    [orgProjectManagers, currentUser, onUpdate],
  )

  // Handle administrator selection from ContactAutocomplete
  const handleAdministratorChange = useCallback(
    (contact: ContactInfo | null) => {
      if (!contact) {
        onUpdate({ administrators: [] })
        return
      }

      const matching = orgAdministrators.find(
        (c) => c.id === contact.id || c.email === contact.email,
      )

      if (matching) {
        const updated = {
          ...matching,
          email: contact.email,
          phone_number: contact.phone || '',
          company: matching.company || currentUser?.company,
        }
        onUpdate({ administrators: [updated] })
      } else {
        const newUser = {
          id: contact.id || 0,
          first_name: contact.firstName,
          last_name: contact.lastName,
          email: contact.email,
          phone_number: contact.phone,
          company: contact.companyName
            ? {
                name: contact.companyName,
                address: contact.companyAddress,
              }
            : undefined,
        } as User
        onUpdate({ administrators: [newUser] })
      }
    },
    [orgAdministrators, currentUser, onUpdate],
  )

  // Handle recipient selection from ContactAutocomplete
  const handleRecipientChange = useCallback(
    (contact: ContactInfo | null) => {
      if (!contact) {
        if (
          templateType === 'BROKER' ||
          templateType === 'THIRD_PARTY_BROKER'
        ) {
          onUpdate({ brokers: [] })
        } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
          onUpdate({ third_party_contractors: [] })
        } else if (templateType === 'SUB_HAULER') {
          onUpdate({ sub_haulers: [] })
        } else if (templateType === 'THIRD_PARTY_SUB_HAULER') {
          onUpdate({ third_party_sub_haulers: [] })
        }
        return
      }

      // Find matching contact from the appropriate list
      let matchingContact: Contact | undefined
      if (templateType === 'BROKER' || templateType === 'THIRD_PARTY_BROKER') {
        matchingContact = brokers.find(
          (c) =>
            c.id === contact.id || c.contact_object.email === contact.email,
        )
      } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
        matchingContact = thirdPartyContractors.find(
          (c) =>
            c.id === contact.id || c.contact_object.email === contact.email,
        )
      } else if (templateType === 'SUB_HAULER') {
        matchingContact = subHaulers.find(
          (c) =>
            c.id === contact.id || c.contact_object.email === contact.email,
        )
      } else if (templateType === 'THIRD_PARTY_SUB_HAULER') {
        matchingContact = subHaulers.find(
          (c) =>
            c.id === contact.id || c.contact_object.email === contact.email,
        )
      }

      // Build user object from contact
      const userFromContact = (c: Contact) => ({
        id: c.contact_object.user_id || c.contact_object.id,
        first_name: c.contact_object.name?.split(' ')[0] || '',
        last_name: c.contact_object.name?.split(' ').slice(1).join(' ') || '',
        email: c.contact_object.email,
        phone_number: c.contact_object.phone_number || '',
        company: c.contact_object.company_name
          ? {
              name: c.contact_object.company_name,
              address: c.contact_object.company_address,
              street_address: c.contact_object.company_street_address,
              city: c.contact_object.company_city,
              state: c.contact_object.company_state,
              zip_code: c.contact_object.company_zip_code,
            }
          : undefined,
      })

      if (matchingContact) {
        const user = userFromContact(matchingContact) as User
        if (
          templateType === 'BROKER' ||
          templateType === 'THIRD_PARTY_BROKER'
        ) {
          onUpdate({ brokers: [user] })
        } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
          onUpdate({ third_party_contractors: [user] })
        } else if (templateType === 'SUB_HAULER') {
          onUpdate({ sub_haulers: [user] })
        } else if (templateType === 'THIRD_PARTY_SUB_HAULER') {
          onUpdate({ third_party_sub_haulers: [user] })
        }
      } else {
        // Manual entry - create a partial user object
        const newUser = {
          id: contact.id || 0,
          first_name: contact.firstName,
          last_name: contact.lastName,
          email: contact.email,
          phone_number: contact.phone,
          company: contact.companyName
            ? {
                name: contact.companyName,
                address: contact.companyAddress,
              }
            : undefined,
        } as User

        if (
          templateType === 'BROKER' ||
          templateType === 'THIRD_PARTY_BROKER'
        ) {
          onUpdate({ brokers: [newUser] })
        } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
          onUpdate({ third_party_contractors: [newUser] })
        } else if (templateType === 'SUB_HAULER') {
          onUpdate({ sub_haulers: [newUser] })
        } else if (templateType === 'THIRD_PARTY_SUB_HAULER') {
          onUpdate({ third_party_sub_haulers: [newUser] })
        }
      }
    },
    [brokers, subHaulers, thirdPartyContractors, templateType, onUpdate],
  )

  // Site supervisor options for the dropdown
  const siteSupervisorOptions = useMemo(
    () =>
      siteSupervisors.map((supervisor) => ({
        label: `${supervisor.firstName} ${supervisor.lastName} (${supervisor.email})`,
        value: supervisor.id.toString(),
        firstName: supervisor.firstName,
        lastName: supervisor.lastName,
        email: supervisor.email,
        phone: supervisor.phone,
      })),
    [siteSupervisors],
  )

  // Handler for when a site supervisor is selected from the dropdown
  const handleSiteSupervisorSelect = (selected: Option | Option[] | null) => {
    const selectedOption = Array.isArray(selected) ? selected[0] : selected
    if (!selectedOption) return

    const supervisor = siteSupervisors.find(
      (s) => s.id.toString() === selectedOption.value,
    )
    if (supervisor) {
      onUpdate({
        site_supervisor_first_name: supervisor.firstName,
        site_supervisor_last_name: supervisor.lastName,
        site_supervisor_email: supervisor.email,
        site_supervisor_phone: supervisor.phone || '',
      })
    }
  }

  // Contractor error only applies in non-downstream flows for CONTRACTOR senders
  const hasContractorError =
    showErrors &&
    !isDownstreamFlow &&
    userStakeholderType === 'CONTRACTOR' &&
    (!data.contractors || data.contractors.length === 0)

  // Parent project error applies in both sub-contract and broker downstream flows
  const hasParentProjectError = showErrors && isDownstreamFlow && !parentProject

  const hasRecipientError = useMemo(() => {
    if (!showErrors) return false
    if (templateType === 'BROKER' || templateType === 'THIRD_PARTY_BROKER') {
      return !data.brokers || data.brokers.length === 0
    }
    if (templateType === 'THIRD_PARTY_CONTRACTOR') {
      return (
        !data.third_party_contractors ||
        data.third_party_contractors.length === 0
      )
    }
    if (templateType === 'SUB_HAULER') {
      return !data.sub_haulers || data.sub_haulers.length === 0
    }
    if (templateType === 'THIRD_PARTY_SUB_HAULER') {
      return (
        !data.third_party_sub_haulers ||
        data.third_party_sub_haulers.length === 0
      )
    }
    return false
  }, [
    showErrors,
    templateType,
    data.brokers,
    data.third_party_contractors,
    data.sub_haulers,
    data.third_party_sub_haulers,
  ])

  // Get stakeholder type for recipient
  const getRecipientStakeholderType = (): StakeholderType[] => {
    if (templateType === 'BROKER' || templateType === 'THIRD_PARTY_BROKER')
      return [StakeholderType.BROKER]
    if (templateType === 'THIRD_PARTY_CONTRACTOR')
      return [StakeholderType.CONTRACTOR]
    if (
      templateType === 'SUB_HAULER' ||
      templateType === 'THIRD_PARTY_SUB_HAULER'
    )
      return [StakeholderType.SUB_HAULER]
    return []
  }

  return (
    <div>
      <h3 className={css(styles.sectionTitle)}>
        Parties and their Contact Information
      </h3>
      <p className={css(styles.requiredNote)}>* Required field</p>

      <h4 className={css(styles.partiesHeader)}>Parties</h4>

      <div className={css(styles.contactsContainer)}>
        {/* Downstream flow: Show Parent Project Selector and Original Project Parties Reference */}
        {/* Applies to both sub-contract (sub-hauler to sub-hauler) and broker downstream (broker to broker) */}
        {isDownstreamFlow && (
          <>
            {/* Parent Project Selector */}
            <ParentProjectSelector
              onSelect={onParentProjectSelect || (() => {})}
              selectedProject={parentProject}
              disabled={readOnly}
              error="Please select a parent project"
              showError={hasParentProjectError}
              senderType={isBrokerDownstream ? 'BROKER' : 'SUB_HAULER'}
            />

            {/* Original Project Parties (read-only reference) */}
            {parentProject && (
              <div className={css(styles.originalSenderBox)}>
                <div className={css(styles.originalSenderHeader)}>
                  <i className="fa-solid fa-info-circle" />
                  <span>Original Project Parties (Reference Only)</span>
                </div>
                <div className={css(styles.originalSenderContent)}>
                  {/* Contractors from parent project */}
                  {parentProject.contractors &&
                    parentProject.contractors.length > 0 && (
                      <div className={css(styles.originalSenderRow)}>
                        <span className={css(styles.originalSenderLabel)}>
                          Contractor:
                        </span>
                        <span>
                          {parentProject.contractors
                            .map(
                              (c) =>
                                `${c.first_name} ${c.last_name}${c.company?.name ? ` (${c.company.name})` : ''}`,
                            )
                            .join(', ')}
                        </span>
                      </div>
                    )}

                  {/* Brokers from parent project */}
                  {parentProject.brokers &&
                    parentProject.brokers.length > 0 && (
                      <div className={css(styles.originalSenderRow)}>
                        <span className={css(styles.originalSenderLabel)}>
                          Broker:
                        </span>
                        <span>
                          {parentProject.brokers
                            .map(
                              (b) =>
                                `${b.first_name} ${b.last_name}${b.company?.name ? ` (${b.company.name})` : ''}`,
                            )
                            .join(', ')}
                        </span>
                      </div>
                    )}

                  {/* Site Supervisors from parent project */}
                  {parentProject.site_supervisors &&
                    parentProject.site_supervisors.length > 0 && (
                      <div className={css(styles.originalSenderRow)}>
                        <span className={css(styles.originalSenderLabel)}>
                          Site Supervisor:
                        </span>
                        <span>
                          {parentProject.site_supervisors
                            .map((s) => `${s.first_name} ${s.last_name}`)
                            .join(', ')}
                        </span>
                      </div>
                    )}

                  {/* Sub Haulers from parent project */}
                  {parentProject.sub_haulers &&
                    parentProject.sub_haulers.length > 0 && (
                      <div className={css(styles.originalSenderRow)}>
                        <span className={css(styles.originalSenderLabel)}>
                          Sub Hauler:
                        </span>
                        <span>
                          {parentProject.sub_haulers
                            .map(
                              (s) =>
                                `${s.first_name} ${s.last_name}${s.company?.name ? ` (${s.company.name})` : ''}`,
                            )
                            .join(', ')}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Contractor - only show for CONTRACTOR senders (not for brokers, sub-haulers, or downstream flows) */}
        {!isDownstreamFlow && userStakeholderType === 'CONTRACTOR' && (
          <>
            {/* Contractor - uses ContactAutocomplete with 3-box layout */}
            {/* teamMembersOnly ensures only team members from the same company are shown */}
            <ContactAutocomplete
              label="Contractor"
              value={contractorContactInfo}
              onChange={handleContractorChange}
              stakeholderTypes={[StakeholderType.CONTRACTOR]}
              teamMembersOnly
              required
              showError={hasContractorError}
              disabled={readOnly}
              placeholder="Select or search for a contractor..."
            />
          </>
        )}

        {/* Recipient (Broker, Third Party Contractor, or Sub Hauler) - uses ContactAutocomplete with 3-box layout */}
        <ContactAutocomplete
          label={templateConfig.recipientLabel}
          value={recipientContactInfo}
          onChange={handleRecipientChange}
          stakeholderTypes={getRecipientStakeholderType()}
          required
          showError={hasRecipientError}
          disabled={readOnly}
          placeholder={`Select or search for a ${templateConfig.recipientLabel.toLowerCase()}...`}
        />

        {/* Project Manager - only show for CONTRACTOR senders (not for downstream flows) */}
        {!isDownstreamFlow && userStakeholderType === 'CONTRACTOR' && (
          <ContactAutocomplete
            label="Project Manager"
            value={projectManagerContactInfo}
            onChange={handleProjectManagerChange}
            teamMembersOnly
            userType="PROJECT_MANAGER"
            disabled={readOnly}
            placeholder="Select or search for a project manager..."
          />
        )}

        {/* Administrator - only show for CONTRACTOR senders (not for downstream flows) */}
        {!isDownstreamFlow && userStakeholderType === 'CONTRACTOR' && (
          <ContactAutocomplete
            label="Administrator"
            value={administratorContactInfo}
            onChange={handleAdministratorChange}
            teamMembersOnly
            userType="ADMINISTRATOR"
            disabled={readOnly}
            placeholder="Select or search for an administrator..."
          />
        )}
      </div>

      {/* Site Supervisor Section - only show for CONTRACTOR senders (not for brokers, sub-haulers, or downstream flows) */}
      {!isDownstreamFlow && userStakeholderType === 'CONTRACTOR' && (
        <div className={css(styles.siteSupervisorSection)}>
          <h4 className={css(styles.subSectionTitle)}>Site Supervisor</h4>

          {/* Quick select from existing site supervisors */}
          <div className={css(styles.formGroup, styles.selectFormGroup)}>
            <label className={css(styles.label)}>
              Select from existing site supervisors
            </label>
            <MultiSelect
              isDisabled={readOnly}
              isLoading={isLoadingData}
              placeholder="Select a site supervisor to autofill"
              options={siteSupervisorOptions}
              value={null}
              onChange={handleSiteSupervisorSelect}
              isMulti={false}
            />
          </div>

          <div className={css(styles.gridContainer)}>
            <div className={css(styles.formGroup)}>
              <label className={css(styles.label)}>First Name</label>
              <Input
                disabled={readOnly}
                placeholder="Enter first name"
                value={data.site_supervisor_first_name || ''}
                onChange={(e) =>
                  onUpdate({ site_supervisor_first_name: e.target.value })
                }
              />
            </div>

            <div className={css(styles.formGroup)}>
              <label className={css(styles.label)}>Last Name</label>
              <Input
                disabled={readOnly}
                placeholder="Enter last name"
                value={data.site_supervisor_last_name || ''}
                onChange={(e) =>
                  onUpdate({ site_supervisor_last_name: e.target.value })
                }
              />
            </div>

            <div className={css(styles.formGroup)}>
              <label className={css(styles.label)}>Phone Number</label>
              <Input
                disabled={readOnly}
                placeholder="Enter phone number"
                value={data.site_supervisor_phone || ''}
                onChange={(e) =>
                  onUpdate({ site_supervisor_phone: e.target.value })
                }
              />
            </div>

            <div className={css(styles.formGroup)}>
              <label className={css(styles.label)}>Email Address</label>
              <Input
                disabled={readOnly}
                placeholder="Enter email address"
                type="email"
                value={data.site_supervisor_email || ''}
                onChange={(e) =>
                  onUpdate({ site_supervisor_email: e.target.value })
                }
                onBlur={(e) => {
                  // Check if we should prompt to save to directory
                  const email = e.target.value?.trim()
                  if (email) {
                    const name =
                      `${data.site_supervisor_first_name || ''} ${data.site_supervisor_last_name || ''}`.trim()
                    checkSiteSupervisorSaveToDirectory({
                      email,
                      name: name || undefined,
                      phoneNumber: data.site_supervisor_phone || undefined,
                    })
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Save to Directory Dialog for Site Supervisor */}
      {showSiteSupervisorDialog && pendingSiteSupervisorContact && (
        <SaveToDirectoryDialog
          email={pendingSiteSupervisorContact.email}
          name={pendingSiteSupervisorContact.name}
          phoneNumber={pendingSiteSupervisorContact.phoneNumber}
          stakeholderTypes={[StakeholderType.SITE_SUPERVISOR]}
          onConfirm={handleSiteSupervisorDialogConfirm}
          onSkip={handleSiteSupervisorDialogCancel}
        />
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  requiredNote: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  partiesHeader: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#374151',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: '8px',
  },
  subSectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#374151',
  },
  contactsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px 24px',
  },
  formGroup: {
    marginBottom: '0',
  },
  selectFormGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
  },
  siteSupervisorSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
  },
  originalSenderBox: {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '8px',
  },
  originalSenderHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '12px',
  },
  originalSenderContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  originalSenderRow: {
    display: 'flex',
    gap: '8px',
    fontSize: '14px',
    color: '#4B5563',
  },
  originalSenderLabel: {
    fontWeight: 500,
    color: '#6B7280',
    minWidth: '80px',
  },
})

export default TemplateContactInfoStep
